import { useRef } from "react";
import { AxiosResponse } from "axios";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { useCreditProposal } from "@repo/core/screens/ProposalEffectiveFlows/Credit/hooks/useCreditProposal";
import { UnicoResource } from "@repo/core/services/resources/unico";
import { newApInstance } from "@repo/core/services/instances/new-api";
import {
  UnicoDocumentProcessResponse,
  UnicoCreateProcessResponse
} from "@repo/core/services/entities/Unico";
import { PreProposalData } from "../../../store/preProposalStore";
import { RequiredDocuments } from "../../../store/preProposalStore";
import { RequiredDocumentTypeEnum } from "@repo/core/enums/proposals";
import { createRetryFunction, resetRetryCount } from "../helpers/retry.helper";
import { MAX_RETRY_ATTEMPTS } from "../constants";
import { uploadUnicoDocuments } from "../services/documentUpload.service";
import {
  navigateBasedOnDocuments,
  hasUnicoDocuments
} from "../services/navigation.service";

interface UseUnicoDocumentsParams {
  proposalData: PreProposalData | null;
  requiredDocuments?: RequiredDocuments[];
  markDocumentAsUploaded: (type: RequiredDocumentTypeEnum) => void;
  uploadDocumentFromBase64: (
    lngProposta: number,
    base64: string,
    params: {
      strTipoDocumento: string;
      strObservacoes: string;
      strCliente: string;
      strCpfPortador: string;
      strAutorizacao: string;
      datDocumentoExterno: string;
      strVisualizarWEB: string;
      contentType?: string;
    }
  ) => Promise<unknown>;
}

export const useUnicoDocuments = ({
  proposalData,
  requiredDocuments,
  markDocumentAsUploaded,
  uploadDocumentFromBase64
}: UseUnicoDocumentsParams) => {
  const { push, replace, resetTo } = useNavigation();
  const { clearCreditProposal } = useCreditProposal();
  const documentRetryCount = useRef(0);

  const getDocumentsWithRetry = async (
    processId: string
  ): Promise<AxiosResponse<UnicoDocumentProcessResponse>> => {
    const unicoResourceForRead = new UnicoResource(newApInstance);

    return createRetryFunction(
      () => unicoResourceForRead.getProcessDocuments(processId),
      {
        maxRetries: MAX_RETRY_ATTEMPTS,
        retryCountRef: documentRetryCount
      }
    );
  };

  const handleUploadDocuments = async (
    processData: UnicoCreateProcessResponse,
    documentData: UnicoDocumentProcessResponse
  ): Promise<Awaited<ReturnType<typeof uploadUnicoDocuments>>> => {
    if (!proposalData?.lngProposta) {
      throw new Error("ID da proposta não encontrado");
    }

    return uploadUnicoDocuments(
      documentData,
      processData,
      proposalData,
      uploadDocumentFromBase64,
      markDocumentAsUploaded
    );
  };

  const handleDocumentError = (): void => {
    resetRetryCount(documentRetryCount);
    navigateBasedOnDocuments(
      requiredDocuments,
      push,
      hasUnicoDocuments(requiredDocuments),
      proposalData,
      replace,
      clearCreditProposal,
      resetTo
    );
  };

  return {
    getDocumentsWithRetry,
    handleUploadDocuments,
    handleDocumentError
  };
};
